/**
 * useShopProductSelection — état d'achat d'UN produit de boutique
 * (sélection de variantes, quantité, prix libre, champs perso).
 *
 * Extrait de `BoutiqueProductSheet` pour être partagé avec la page produit
 * dédiée `/boutique/[slug]/[productSlug]` : une seule autorité de calcul, donc
 * aucune divergence possible entre les deux surfaces.
 *
 * ⚠️ CORRECTIONS UX PORTÉES ICI (audit fiche produit, référence Shopify) :
 *
 *  a) **Épuisé ≠ inexistant.** L'ancien `isValueDisabled` retournait `true`
 *     dans deux cas opposés — « on ne fait pas de blanc » et « le blanc est
 *     parti » — rendus par un unique barré. Ils sont désormais scindés en
 *     `isValueSoldOut` / `isValueUnavailable`, avec des libellés distincts
 *     (`valueStateLabel`) et un badge « Épuisé » côté template (canon
 *     `BslugStepTickets`, jamais de barré).
 *
 *  c) **Le critère manquant est NOMMÉ.** `missingAxes` expose les axes non
 *     renseignés — l'information était calculée puis jetée par
 *     `allAxesSelected`. Le bouton reste ACTIF (NN/G) et
 *     `firstMissingAxis` permet de scroller vers le bon sélecteur.
 *
 *  e) **Stock calculable au plus tôt.** `remainingForDisplay` retombe sur le
 *     restant CUMULÉ des variantes compatibles avec la sélection PARTIELLE :
 *     l'acheteur voit « 3 restants » sans avoir choisi tous les axes.
 *
 * ⚠️ MONTANTS EN EUROS (convention `ticket_types.price`).
 */
import type { Ref } from "vue";
import type { ShopProduct, ShopProductVariant } from "~/types/shop";
import {
  getVariantRemaining,
  isVariantSoldOut,
} from "~/composables/useShopCart";
import {
  shopProductHasPriceRange,
  shopProductMinPrice,
} from "~/utils/shopPricing";

/** État d'une valeur d'axe — trois cas DISTINCTS, jamais confondus. */
export type ShopAxisValueState = "available" | "sold_out" | "unavailable";

export interface UseShopProductSelectionOptions {
  product: Ref<ShopProduct | null>;
  canBuy: Ref<boolean>;
  /** Plafond ajoutable (stock − panier, max_per_order − panier). */
  getMaxForLine: (
    product: ShopProduct,
    variant: ShopProductVariant | null,
  ) => number;
}

export function useShopProductSelection(opts: UseShopProductSelectionOptions) {
  const { t } = useI18n();
  // Montants dans la langue de la page.
  const { formatCurrency } = useFormatLangue();
  const { product, canBuy, getMaxForLine } = opts;

  // ─── État local ──────────────────────────────────────────────────
  const selectedValues = ref<Record<string, string>>({});
  const quantity = ref(1);
  const customPrice = ref<number>(0);
  const customData = ref<Record<string, unknown>>({});
  /** Axe mis en évidence après un clic « Ajouter » incomplet (feedback c). */
  const highlightedAxis = ref<string | null>(null);

  /** Réinitialise tout l'état (changement de produit / ouverture de fiche). */
  function reset() {
    selectedValues.value = {};
    quantity.value = 1;
    customPrice.value = Number(product.value?.min_price || 0);
    customData.value = {};
    highlightedAxis.value = null;
  }

  // ─── Variantes ───────────────────────────────────────────────────
  const axes = computed(() => product.value?.variant_axes || []);
  const hasAxes = computed(() => axes.value.length > 0);
  const activeVariants = computed(() =>
    (product.value?.variants || []).filter((v) => v.is_active),
  );

  /** Axes encore non renseignés — l'info que l'ancien code jetait (défaut c). */
  const missingAxes = computed(() =>
    axes.value
      .filter((axis) => !selectedValues.value[axis.name])
      .map((axis) => axis.name),
  );
  const allAxesSelected = computed(() => missingAxes.value.length === 0);
  const firstMissingAxis = computed<string | null>(
    () => missingAxes.value[0] ?? null,
  );

  /** Variante correspondant EXACTEMENT à la sélection courante. */
  const matchingVariant = computed<ShopProductVariant | null>(() => {
    if (!product.value) return null;
    if (!hasAxes.value) {
      return (
        activeVariants.value.find((v) => !v.combination) ||
        activeVariants.value[0] ||
        null
      );
    }
    if (!allAxesSelected.value) return null;
    return (
      activeVariants.value.find((v) => {
        const combo = v.combination || {};
        return axes.value.every(
          (axis) => combo[axis.name] === selectedValues.value[axis.name],
        );
      }) || null
    );
  });

  /**
   * Variantes portant `value` sur `axisName` et compatibles avec les AUTRES
   * axes déjà choisis. Base commune des trois prédicats d'état.
   */
  function variantsForValue(
    axisName: string,
    value: string,
  ): ShopProductVariant[] {
    return activeVariants.value.filter((v) => {
      const combo = v.combination || {};
      if (combo[axisName] !== value) return false;
      for (const axis of axes.value) {
        if (axis.name === axisName) continue;
        const selected = selectedValues.value[axis.name];
        if (selected && combo[axis.name] !== selected) return false;
      }
      return true;
    });
  }

  /**
   * État d'une valeur d'axe — LA correction du défaut le plus grave :
   *  - `unavailable` : aucune variante ne propose cette combinaison
   *    (« on ne fait pas de blanc ») → désactivé + `title` explicite ;
   *  - `sold_out`    : des variantes existent mais toutes épuisées
   *    (« le blanc est parti ») → badge « Épuisé », lisible et annoncé ;
   *  - `available`   : au moins une variante achetable.
   */
  function valueState(axisName: string, value: string): ShopAxisValueState {
    const candidates = variantsForValue(axisName, value);
    if (candidates.length === 0) return "unavailable";
    return candidates.every((v) => isVariantSoldOut(v)) ? "sold_out" : "available";
  }

  function isValueSoldOut(axisName: string, value: string): boolean {
    return valueState(axisName, value) === "sold_out";
  }
  function isValueUnavailable(axisName: string, value: string): boolean {
    return valueState(axisName, value) === "unavailable";
  }

  /**
   * Libellé d'accessibilité d'une valeur (WCAG 1.3.1 / 1.4.1 — un barré nu
   * n'est PAS annoncé par un lecteur d'écran, cf. G117).
   */
  function valueAriaLabel(axisName: string, value: string): string {
    switch (valueState(axisName, value)) {
      case "sold_out":
        return t("boutique.productSelection.ariaValeurEpuisee", { axisName, value });
      case "unavailable":
        return t("boutique.productSelection.ariaValeurIndisponible", { axisName, value });
      default:
        return t("boutique.productSelection.ariaValeur", { axisName, value });
    }
  }

  /** Texte du `title` natif (survol souris — complète l'aria-label). */
  function valueTitle(axisName: string, value: string): string | undefined {
    switch (valueState(axisName, value)) {
      case "sold_out":
        return t("boutique.productSelection.titreValeurEpuisee", { value });
      case "unavailable":
        return t("boutique.productSelection.titreValeurIndisponible", { value });
      default:
        return undefined;
    }
  }

  // ─── Type de produit ─────────────────────────────────────────────
  /**
   * Produit NUMÉRIQUE (migration 166) : fichier livré par lien après paiement.
   * Ni variantes, ni stock, ni livraison.
   */
  const isDigital = computed(() => product.value?.product_type === "digital");

  // ─── Prix ────────────────────────────────────────────────────────
  const minPrice = computed(() => Number(product.value?.min_price || 0));

  const unitPrice = computed(() => {
    const p = product.value;
    if (!p) return 0;
    if (p.pricing === "free") return 0;
    if (p.pricing === "flexible") return Math.max(0, Number(customPrice.value || 0));
    return Number(matchingVariant.value?.price_override ?? p.price ?? 0);
  });

  /** Le prix dépend-il encore d'un choix non fait ? (défaut d) */
  const priceIsIndicative = computed(() => {
    const p = product.value;
    if (!p || p.pricing !== "fixed") return false;
    return !matchingVariant.value && shopProductHasPriceRange(p);
  });

  /**
   * Libellé de prix affiché DANS la barre d'action, à portée immédiate du
   * bouton (défaut d) : « À partir de 15 € » tant qu'aucune variante n'est
   * choisie, puis le prix exact.
   */
  const priceLabel = computed(() => {
    const p = product.value;
    if (!p) return "";
    if (p.pricing === "free") return t("boutique.commun.gratuit");
    if (p.pricing === "flexible") {
      const amount = Math.max(0, Number(customPrice.value || 0));
      return amount > 0 ? formatCurrency(amount) : t("boutique.productSelection.prixLibre");
    }
    if (priceIsIndicative.value) {
      return t("boutique.productSelection.aPartirDe", { montant: formatCurrency(shopProductMinPrice(p)) });
    }
    return unitPrice.value > 0 ? formatCurrency(unitPrice.value) : t("boutique.commun.gratuit");
  });

  /** Total de la ligne (prix unitaire × quantité) en EUROS. */
  const lineTotal = computed(
    () => Math.round(unitPrice.value * quantity.value * 100) / 100,
  );

  // ─── Stock ───────────────────────────────────────────────────────
  const variantRemaining = computed(() => getVariantRemaining(matchingVariant.value));
  const variantSoldOut = computed(() => isVariantSoldOut(matchingVariant.value));

  /**
   * Restant AFFICHABLE dès qu'il est calculable (défaut e) : restant exact de
   * la variante quand elle est déterminée, sinon somme des restants des
   * variantes encore compatibles avec la sélection PARTIELLE. `null` =
   * illimité (au moins une variante sans limite) ou produit numérique.
   */
  const remainingForDisplay = computed<number | null>(() => {
    if (isDigital.value) return null;
    if (matchingVariant.value) return variantRemaining.value;

    const compatible = activeVariants.value.filter((v) => {
      const combo = v.combination || {};
      return axes.value.every((axis) => {
        const selected = selectedValues.value[axis.name];
        return !selected || combo[axis.name] === selected;
      });
    });
    if (compatible.length === 0) return null;

    let total = 0;
    for (const v of compatible) {
      const remaining = getVariantRemaining(v);
      if (remaining === null) return null;
      total += remaining;
    }
    return total;
  });

  /** Le restant affiché porte-t-il sur une sélection encore partielle ? */
  const remainingIsPartial = computed(
    () => !matchingVariant.value && remainingForDisplay.value !== null,
  );

  /** Produit entièrement épuisé (toutes variantes actives à 0). */
  const productSoldOut = computed(() => {
    if (activeVariants.value.length === 0) return false;
    return activeVariants.value.every((v) => isVariantSoldOut(v));
  });

  // ─── Quantité ────────────────────────────────────────────────────
  const maxQuantity = computed(() => {
    if (!product.value) return 0;
    const max = getMaxForLine(product.value, matchingVariant.value);
    return Number.isFinite(max) ? max : 99;
  });

  watch([matchingVariant, maxQuantity], () => {
    if (quantity.value > maxQuantity.value) {
      quantity.value = Math.max(1, maxQuantity.value);
    }
  });

  function decrementQuantity() {
    quantity.value = Math.max(1, quantity.value - 1);
  }
  function incrementQuantity() {
    quantity.value = Math.min(maxQuantity.value, quantity.value + 1);
  }

  // ─── Prix libre ──────────────────────────────────────────────────
  const flexibleTooLow = computed(
    () =>
      product.value?.pricing === "flexible" &&
      minPrice.value > 0 &&
      Number(customPrice.value || 0) < minPrice.value,
  );

  // ─── Champs perso de ligne ───────────────────────────────────────
  const lineCustomFields = computed(() => product.value?.custom_fields || []);
  const lineFieldsValid = computed(() => {
    for (const f of lineCustomFields.value) {
      if (!f?.required) continue;
      const v = customData.value[f.key];
      if (
        v === undefined ||
        v === null ||
        v === "" ||
        (Array.isArray(v) && v.length === 0)
      ) {
        return false;
      }
    }
    return true;
  });

  // ─── Validité de l'ajout ─────────────────────────────────────────
  const canAdd = computed(() => {
    if (!product.value || !canBuy.value) return false;
    if (hasAxes.value && !allAxesSelected.value) return false;
    if (hasAxes.value && !matchingVariant.value) return false;
    if (variantSoldOut.value) return false;
    if (maxQuantity.value <= 0) return false;
    if (flexibleTooLow.value) return false;
    if (!lineFieldsValid.value) return false;
    return quantity.value >= 1;
  });

  /**
   * Message expliquant ce qui BLOQUE, en NOMMANT le critère manquant
   * (défaut c) : « Choisissez : Taille » et non « Sélectionnez une option
   * pour chaque critère ». Vide quand tout est bon.
   */
  const blockingReason = computed(() => {
    if (!product.value) return "";
    if (!canBuy.value) return "";
    if (hasAxes.value && missingAxes.value.length > 0) {
      return t("boutique.productSelection.choisissez", { axes: missingAxes.value.join(", ") });
    }
    if (hasAxes.value && !matchingVariant.value) {
      return t("boutique.productSelection.combinaisonIndisponible");
    }
    if (variantSoldOut.value || maxQuantity.value <= 0) {
      return t("boutique.productSelection.selectionEpuisee");
    }
    if (flexibleTooLow.value) {
      return t("boutique.productSelection.montantMinimum", { montant: formatCurrency(minPrice.value) });
    }
    if (!lineFieldsValid.value) return t("boutique.productSelection.champsObligatoires");
    return "";
  });

  function selectValue(axisName: string, value: string) {
    if (isValueUnavailable(axisName, value)) return;
    if (selectedValues.value[axisName] === value) {
      const { [axisName]: _removed, ...rest } = selectedValues.value;
      selectedValues.value = rest;
    } else {
      selectedValues.value = { ...selectedValues.value, [axisName]: value };
    }
    if (highlightedAxis.value === axisName) highlightedAxis.value = null;
  }

  /** Payload d'ajout au panier — `null` si la sélection n'est pas valide. */
  function buildAddPayload() {
    const p = product.value;
    if (!p || !canAdd.value) return null;
    return {
      product: p,
      variant: matchingVariant.value,
      quantity: quantity.value,
      custom_price:
        p.pricing === "flexible" ? Number(customPrice.value || 0) : undefined,
      custom_data:
        Object.keys(customData.value).length > 0
          ? { ...customData.value }
          : undefined,
    };
  }

  return {
    // état
    selectedValues,
    quantity,
    customPrice,
    customData,
    highlightedAxis,
    reset,
    // variantes
    axes,
    hasAxes,
    activeVariants,
    missingAxes,
    firstMissingAxis,
    allAxesSelected,
    matchingVariant,
    valueState,
    isValueSoldOut,
    isValueUnavailable,
    valueAriaLabel,
    valueTitle,
    selectValue,
    // produit
    isDigital,
    productSoldOut,
    // prix
    minPrice,
    unitPrice,
    priceLabel,
    priceIsIndicative,
    lineTotal,
    flexibleTooLow,
    // stock
    variantRemaining,
    variantSoldOut,
    remainingForDisplay,
    remainingIsPartial,
    // quantité
    maxQuantity,
    decrementQuantity,
    incrementQuantity,
    // champs perso
    lineCustomFields,
    lineFieldsValid,
    // ajout
    canAdd,
    blockingReason,
    buildAddPayload,
  };
}
